import { useState, useCallback, useEffect } from 'react';
import { toggleLike } from '../../lib/supabase/social';

// Cache for like status to prevent repeated checks
const likeStatusCache = new Map<string, boolean>();

interface UsePostInteractionsProps {
  postId: string;
  initialLikes: number;
  initialShares: number;
  batchLikes?: Record<string, boolean>;
}

export function usePostInteractions({
  postId,
  initialLikes,
  initialShares,
  batchLikes
}: UsePostInteractionsProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(initialLikes);
  const [sharesCount, setSharesCount] = useState(initialShares);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Treat temp-post IDs with immediate optimistic UI only
  const isTemporaryPost = postId.startsWith('temp-');

  // Initialize like status from batch or cache
  useEffect(() => {
    if (batchLikes?.[postId] !== undefined) {
      setIsLiked(batchLikes[postId]);
    } else if (likeStatusCache.has(postId)) {
      setIsLiked(likeStatusCache.get(postId)!);
    }
  }, [postId, batchLikes]);

  const handleLike = useCallback(async () => {
    if (isLoading) return;

    const wasLiked = isLiked;
    const wasCount = likesCount;
    const newLiked = !wasLiked;

    // For temporary posts, just toggle locally
    if (isTemporaryPost) {
      setIsLiked(newLiked);
      setLikesCount(wasCount + (newLiked ? 1 : -1));
      return;
    }

    // Optimistic update
    setIsLiked(newLiked);
    setLikesCount(wasCount + (newLiked ? 1 : -1));
    setIsLoading(true);
    setError(null);

    try {
      // Call the API
      const confirmedLiked = await toggleLike(postId);

      // Reconcile if server disagrees
      if (confirmedLiked !== newLiked) {
        setIsLiked(confirmedLiked);
        setLikesCount(wasCount + (confirmedLiked ? 1 : -1));
      }

      // Cache for next time
      likeStatusCache.set(postId, confirmedLiked);
    } catch (err) {
      console.error('Error toggling like:', err);
      // Revert on error
      setIsLiked(wasLiked);
      setLikesCount(wasCount);
      if (!(err instanceof Error && err.message.includes('Failed to fetch'))) {
        setError(err instanceof Error ? err.message : 'Failed to toggle like');
      }
    } finally {
      setIsLoading(false);
    }
  }, [postId, isLiked, likesCount, isLoading, isTemporaryPost]);

  const handleShare = useCallback(() => {
    setSharesCount(prev => prev + 1);
  }, []);

  return {
    isLiked,
    likesCount,
    sharesCount,
    isLoading,
    error,
    handleLike,
    handleShare,
  };
}
